/**
 * 商家端 - 订单中心（列表 + 四流视图详情）
 * 四流视图是 PRD 02 §3 / 07 的核心：一屏看全合同/资金/发票/物流证据链
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, Table, Button, Badge, useApi, money, time, Loading } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Order {
  order_id: string
  amount_total: string
  amount_refunded: string
  status: string
  confirm_time: string | null
  logistics_no: string | null
  synced_at: string
  has_settlement?: boolean
  has_invoice?: boolean
}

/** 处理中 = PAID 但钱或票还没处理完 */
function isProcessing(o: Order): boolean {
  return o.status === 'PAID' && (!o.has_settlement || !o.has_invoice)
}

export function SellerOrders() {
  const list = useApi<Order[]>(async () => api.get('/orders'), [])
  const [searchParams, setSearchParams] = useSearchParams()
  const selected = searchParams.get('detail')

  if (selected) {
    return <OrderDetail orderId={selected} onBack={() => { setSearchParams({}, { replace: true }); list.refresh() }} />
  }

  const all = list.data ?? []
  const processing = all.filter(isProcessing)
  const done = all.filter((o) => !isProcessing(o))

  return (
    <div>
      <h2 style={{ margin: 0 }}>订单中心</h2>

      <Card title={`处理中（${processing.length}）`}>
        <Table<Order>
          rows={processing}
          empty={list.loading ? '加载中...' : '无处理中订单'}
          columns={[
            { key: 'order_id', title: '订单号' },
            { key: 'amount_total', title: '金额', render: (r) => money(r.amount_total) },
            { key: 'settle', title: '分账', render: (r) => (r.has_settlement ? '✓' : '待处理') },
            { key: 'invoice', title: '开票', render: (r) => (r.has_invoice ? '✓' : '待处理') },
            { key: 'synced_at', title: '同步时间', render: (r) => time(r.synced_at) },
            {
              key: 'action', title: '操作',
              render: (r) => <Button size="sm" onClick={() => setSearchParams({ detail: r.order_id })}>详情</Button>,
            },
          ]}
        />
      </Card>

      <Card title={`已处理（${done.length}）`}>
        <Table<Order>
          rows={done}
          empty="无已处理订单"
          columns={[
            { key: 'order_id', title: '订单号' },
            { key: 'amount_total', title: '金额', render: (r) => money(r.amount_total) },
            { key: 'amount_refunded', title: '已退款', render: (r) => (Number(r.amount_refunded) > 0 ? money(r.amount_refunded) : '-') },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            {
              key: 'action', title: '操作',
              render: (r) => <Button size="sm" onClick={() => setSearchParams({ detail: r.order_id })}>详情</Button>,
            },
          ]}
        />
      </Card>

      <HelpBubble pageKey="seller-orders" title="订单中心">
        <p>订单按处理进度分两组：<b>处理中</b>（已支付但分账/开票未完成）和<b>已处理</b>（钱票都完成或已退款）。</p>
        <p>点「详情」进入订单完整视图：支付详情、合同、分账、物流、数电票，全部关联订单 ID。</p>
      </HelpBubble>
    </div>
  )
}

interface Detail {
  order: Order & {
    platform_order_id: string
    source_platform: string
    partnership_id: string
    buyer_ref: unknown
  }
  contract: { contract_id: string; commission_rate: string; signed_at: string; core_clauses: unknown } | null
  settlements: Array<{ settlement_id: string; status: string; seller_amount: string; factory_amount_net: string; platform_fee: string; bank_serial_no: string; executed_at: string | null }>
  invoices: Array<{ invoice_id: string; invoice_category: string; issuer_id: string; issuer_role: string; amount: string; tax_rate: string; remark: string; item_name: string; status: string; invoice_no: string; pdf_url: string | null; issued_at: string | null }>
  logistics: Array<{ logistics_id: string; carrier: string; tracking_no: string; shipper_id: string; status: string; uploaded_at: string }>
}

function OrderDetail({ orderId, onBack }: { orderId: string; onBack: () => void }) {
  const detail = useApi<Detail>(async () => api.get(`/orders/${orderId}`), [orderId])
  const [confirming, setConfirming] = useState(false)

  if (detail.loading) return <Loading />
  if (detail.error || !detail.data) return <Loading error={detail.error || '无数据'} />

  const { order, contract, settlements, invoices, logistics } = detail.data

  async function confirm() {
    setConfirming(true)
    try {
      await api.post(`/orders/${orderId}/confirm`)
      detail.refresh()
    } catch {
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button size="sm" variant="default" onClick={onBack}>
          ← 返回
        </Button>
        <h2 style={{ margin: 0 }}>
          订单 {orderId} <Badge status={order.status} />
        </h2>
      </div>

      {/* 订单基本信息 + 操作 */}
      <Card title="订单信息">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 13 }}>
          <Field label="电商原单号" value={order.platform_order_id} />
          <Field label="来源平台" value={order.source_platform} />
          <Field label="订单金额" value={money(order.amount_total)} />
          <Field label="确认收货时间" value={order.confirm_time ? time(order.confirm_time) : '-'} />
          <Field label="物流单号" value={order.logistics_no ?? '未回传'} />
          <Field label="合作ID" value={order.partnership_id} />
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          {order.status === 'PAID' && !order.confirm_time && (
            <Button onClick={confirm} disabled={confirming}>
              {confirming ? '处理中...' : '确认收货'}
            </Button>
          )}
          {order.status === 'PAID' && order.confirm_time && (
            <div style={{ color: '#166534', fontSize: 13 }}>
              ✓ 已支付 · 确认收货 {time(order.confirm_time)}
              {settlements.length > 0 && ' · 已分账'}
              {invoices.filter((i) => i.status === 'ISSUED').length > 0 && ' · 已开票'}
            </div>
          )}
          {order.status === 'REFUNDED' && (
            <div style={{ color: '#991b1b' }}>已退款（{money(order.amount_refunded)}）</div>
          )}
        </div>
      </Card>

      {/* 关联详情，全部关联到订单 ID */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* 支付详情 */}
        <Card title="💳 支付详情">
          <div style={{ fontSize: 13 }}>
            <Field label="支付状态" value={order.status === 'PAID' ? '已支付' : order.status === 'REFUNDED' ? '已退款' : order.status} />
            <Field label="订单金额" value={money(order.amount_total)} />
            <Field label="支付方式" value="电商平台在线支付（抖音/拼多多/快手）" />
            <Field label="电商原单号" value={order.platform_order_id} />
            <Field label="来源平台" value={order.source_platform} />
            <Field label="同步时间" value={time(order.synced_at)} />
            {order.status === 'REFUNDED' && Number(order.amount_refunded) > 0 && (
              <div style={{ marginTop: 8, color: '#991b1b' }}>已退款：{money(order.amount_refunded)}</div>
            )}
          </div>
        </Card>

        {/* 合同流 */}
        <Card title="📋 合同详情">
          {contract ? (
            <div style={{ fontSize: 13 }}>
              <Field label="合同ID" value={contract.contract_id} />
              <Field label="佣金率（服务费占比）" value={`${(parseFloat(contract.commission_rate) * 100).toFixed(1)}%`} />
              <Field label="签署时间" value={contract.signed_at ? time(contract.signed_at) : '-'} />
              <Field label="合作ID" value={order.partnership_id} />
              <div style={{ marginTop: 8, color: '#888' }}>核心条款已存证（哈希链），不可篡改</div>
            </div>
          ) : (
            <div style={{ color: '#888' }}>无合同</div>
          )}
        </Card>

        {/* 资金流（分账详情） */}
        <Card title="💰 分账详情">
          {settlements.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              {settlements.map((s) => (
                <div key={s.settlement_id} style={{ marginBottom: 8 }}>
                  <div>分账 <Badge status={s.status} /> · 流水 {s.bank_serial_no}</div>
                  {s.executed_at && <div style={{ color: '#888' }}>执行时间：{time(s.executed_at)}</div>}
                  <div style={{ marginTop: 4 }}>商家服务费：{money(s.seller_amount)}</div>
                  <div>工厂货款(实收)：{money(s.factory_amount_net)}</div>
                  <div>平台抽佣：{money(s.platform_fee)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#888' }}>未分账{order.status === 'PAID' ? '（确认收货后自动分账）' : ''}</div>
          )}
        </Card>

        {/* 物流详情（电商拉取） */}
        <Card title="📦 物流详情">
          {logistics.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              {logistics.map((l) => (
                <div key={l.logistics_id} style={{ marginBottom: 8 }}>
                  <Field label="物流公司" value={l.carrier} />
                  <Field label="运单号" value={l.tracking_no} />
                  <Field label="物流状态" value={l.status} />
                  <Field label="寄件人" value={`${l.shipper_id}（工厂直发）`} />
                  <Field label="同步时间" value={time(l.uploaded_at)} />
                  <div style={{ marginTop: 4, color: '#888' }}>物流信息从电商平台同步</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#888' }}>无物流信息</div>
          )}
        </Card>
      </div>

      {/* 发票详情（含数电票图片） */}
      <Card title="🧾 发票详情（数电票）">
        {invoices.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {invoices.map((inv) => (
              <div key={inv.invoice_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {inv.pdf_url && inv.status === 'ISSUED' ? (
                  <img src={inv.pdf_url} alt={inv.invoice_no} style={{ width: '100%', display: 'block', borderBottom: '1px solid #e5e7eb' }} />
                ) : (
                  <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', color: '#888', fontSize: 13 }}>
                    {inv.status === 'ISSUING' ? '开票中...' : inv.status === 'FAILED' ? '开票失败' : '无预览'}
                  </div>
                )}
                <div style={{ padding: 10, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {inv.invoice_category === 'GOODS' ? '商品票（13%）' : '服务费票（6%）'} <Badge status={inv.status} />
                  </div>
                  <div style={{ color: '#666' }}>发票号：{inv.invoice_no || '-'}</div>
                  <div style={{ color: '#666' }}>金额：{money(inv.amount)} · 销售方：{inv.issuer_role === 'FACTORY' ? '工厂' : '商家'}</div>
                  <div style={{ color: '#999', marginTop: 4 }}>备注：{inv.remark}</div>
                  {inv.issued_at && <div style={{ color: '#999' }}>开票：{time(inv.issued_at)}</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#888', fontSize: 13 }}>未开票（分账成功后自动开票）</div>
        )}
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}
